import debounce from 'lodash/debounce';
import { storeToRefs } from 'pinia';
import { computed, nextTick, onScopeDispose, ref, watch } from 'vue';
import type { Ref } from 'vue';
import type { RoutePlan } from '@gpx-spa/geo-domain';
import type { ShipmentFormStopRow } from '@/stores/useShipmentFormStore';
import { useShipmentFormStore } from '@/stores/useShipmentFormStore';
import { useShipmentFormAutofillSettingsStore } from '@/stores/useShipmentFormAutofillSettingsStore';
import { COMPOSER_DEBOUNCE } from '@/components/shipment/form/domain/constants';
import { computeStopsRouteFingerprint } from '@/utils/compute-stops-route-fingerprint';
import { SHIPMENT_COMPOSER_ANCHOR_CONFLICT_THRESHOLD_MS } from '@/components/shipment/form/utils/shipment-form-autofill-constants';
import {
  allLegDurationsKnown,
  computeAutofillPatches,
  detectAnchorConflict,
  legDrivingSecondsFromRoutePlan,
  manualSlotKey,
  solveOriginDepartureMsForDestinationArrival,
  type FormPlannedSlot,
} from '@/components/shipment/form/utils/shipment-form-stop-time-autofill';

export function useShipmentFormStopTimeAutofill(params: {
  routePlan: Ref<RoutePlan | null>;
  stops: Ref<ShipmentFormStopRow[]>;
  selectedByPathId: Ref<Record<string, boolean>>;
}) {
  const { routePlan, stops, selectedByPathId } = params;
  const formStore = useShipmentFormStore();
  const { manualPlannedSlots } = storeToRefs(formStore);
  const autofillSettings = useShipmentFormAutofillSettingsStore();
  const {
    helpFillEnabled,
    showConflictWarnings,
    dwellMs,
    maxDrivingMs,
    restMs,
    lastConflictResolutionMode,
  } = storeToRefs(autofillSettings);
  const suppressPlannedTimeManualMark = ref(false);
  const anchorConflictDialogVisible = ref(false);
  const conflictResolutionChoice = ref<'origin' | 'destination'>('origin');
  const suppressConflictDialog = ref(false);

  const lastAppliedFingerprint = ref('');

  const autofillInputFingerprint = computed(() => {
    const leg = legDrivingSecondsFromRoutePlan(
      routePlan.value,
      stops.value.length,
      selectedByPathId.value,
    );
    return JSON.stringify({
      fp: computeStopsRouteFingerprint(stops.value),
      leg,
      dwell: dwellMs.value,
      maxD: maxDrivingMs.value,
      rest: restMs.value,
      help: helpFillEnabled.value,
    });
  });

  /** Re-run autofill when the user edits planned times (route fingerprint alone does not change). */
  const stopsPlannedTimesFingerprint = computed(() =>
    stops.value
      .map(
        (s) =>
          `${s.plannedDeparture?.getTime() ?? ''}:${s.plannedArrival?.getTime() ?? ''}`,
      )
      .join('|'),
  );

  const manualSlotsFingerprint = computed(() =>
    [...manualPlannedSlots.value].sort().join(','),
  );

  /** After each successful autofill run; used to detect user edits to planned times. */
  const prevStopsTimesFingerprint = ref('');

  watch(autofillInputFingerprint, (v, prev) => {
    if (prev !== undefined && v !== prev) {
      suppressConflictDialog.value = false;
    }
  });

  watch(
    () => stops.value.map((s) => s.key).join('\n'),
    () => {
      const allowed = new Set(stops.value.map((s) => s.key));
      const next = new Set<string>();
      for (const entry of manualPlannedSlots.value) {
        const colon = entry.indexOf(':');
        const stopKey = colon === -1 ? entry : entry.slice(0, colon);
        if (allowed.has(stopKey)) {
          next.add(entry);
        }
      }
      manualPlannedSlots.value = next;
    },
  );

  watch(
    lastConflictResolutionMode,
    (m) => {
      conflictResolutionChoice.value = m;
    },
    { immediate: true },
  );

  function isManual(stopKey: string, slot: FormPlannedSlot): boolean {
    return manualPlannedSlots.value.has(manualSlotKey(stopKey, slot));
  }

  function onUserPlannedTimeEdit(
    stopKey: string,
    slot: FormPlannedSlot,
    value: Date | null,
  ) {
    if (suppressPlannedTimeManualMark.value) {
      return;
    }
    if (value == null) {
      formStore.unmarkPlannedSlotManual(stopKey, slot);
    } else {
      formStore.markPlannedSlotManual(stopKey, slot);
    }
  }

  function applyPatches(patches: ReturnType<typeof computeAutofillPatches>) {
    suppressPlannedTimeManualMark.value = true;
    for (const p of patches) {
      const row: Partial<ShipmentFormStopRow> = {};
      if (p.plannedDeparture !== undefined) {
        row.plannedDeparture = p.plannedDeparture ?? null;
      }
      if (p.plannedArrival !== undefined) {
        row.plannedArrival = p.plannedArrival ?? null;
      }
      if (Object.keys(row).length > 0) {
        formStore.patchStop(p.key, row);
      }
    }
    void nextTick(() => {
      suppressPlannedTimeManualMark.value = false;
    });
  }

  function runAutofillCore() {
    if (!helpFillEnabled.value) {
      return;
    }
    const n = stops.value.length;
    if (n < 2) {
      return;
    }

    const legDriveS = legDrivingSecondsFromRoutePlan(
      routePlan.value,
      n,
      selectedByPathId.value,
    );

    const snap = stops.value.map((s) => ({
      key: s.key,
      plannedDeparture: s.plannedDeparture,
      plannedArrival: s.plannedArrival,
    }));

    const firstStop = stops.value[0]!;
    const lastStop = stops.value[n - 1]!;
    const oDep = firstStop.plannedDeparture;
    const dArr = lastStop.plannedArrival;

    if (
      showConflictWarnings.value &&
      !suppressConflictDialog.value &&
      oDep &&
      dArr &&
      isManual(firstStop.key, 'plannedDeparture') &&
      isManual(lastStop.key, 'plannedArrival') &&
      allLegDurationsKnown(legDriveS)
    ) {
      const conflict = detectAnchorConflict({
        originDepartureMs: oDep.getTime(),
        destinationArrivalMs: dArr.getTime(),
        stops: snap,
        legDriveS,
        dwellMs: dwellMs.value,
        maxDriveMs: maxDrivingMs.value,
        restMs: restMs.value,
        thresholdMs: SHIPMENT_COMPOSER_ANCHOR_CONFLICT_THRESHOLD_MS,
      });
      if (conflict) {
        anchorConflictDialogVisible.value = true;
        conflictResolutionChoice.value = lastConflictResolutionMode.value;
        return;
      }
    }

    const timesFpNow = stopsPlannedTimesFingerprint.value;
    const plannedTimesChanged =
      prevStopsTimesFingerprint.value !== '' &&
      timesFpNow !== prevStopsTimesFingerprint.value;

    const fp = autofillInputFingerprint.value;
    const routeRefresh =
      lastAppliedFingerprint.value !== '' &&
      fp !== lastAppliedFingerprint.value;

    const persistedStopKeys = new Set(
      stops.value
        .filter(
          (s) =>
            typeof s.persistedStopId === 'number' && s.persistedStopId > 0,
        )
        .map((s) => s.key),
    );

    const patches = computeAutofillPatches({
      stops: snap,
      legDriveS,
      dwellMs: dwellMs.value,
      maxDriveMs: maxDrivingMs.value,
      restMs: restMs.value,
      manualSlots: manualPlannedSlots.value,
      persistedStopKeys,
      routeRefresh,
      plannedTimesChanged,
    });

    if (patches.length > 0) {
      applyPatches(patches);
    }
    lastAppliedFingerprint.value = fp;
    prevStopsTimesFingerprint.value = stopsPlannedTimesFingerprint.value;
  }

  const debouncedRun = debounce(
    () => runAutofillCore(),
    COMPOSER_DEBOUNCE.autofillTimesMs,
  );

  onScopeDispose(() => {
    debouncedRun.cancel();
  });

  watch(
    [
      autofillInputFingerprint,
      helpFillEnabled,
      stopsPlannedTimesFingerprint,
      manualSlotsFingerprint,
    ],
    () => {
      if (!helpFillEnabled.value) {
        debouncedRun.cancel();
        return;
      }
      if (suppressPlannedTimeManualMark.value) {
        return;
      }
      debouncedRun();
    },
    { flush: 'post', immediate: true },
  );

  function clearAllTimesExcept(mode: 'origin' | 'destination') {
    const rows = stops.value;
    const last = rows.length - 1;
    for (let i = 0; i < rows.length; i++) {
      const s = rows[i]!;
      if (mode === 'origin' && i === 0) {
        formStore.patchStop(s.key, { plannedArrival: null });
        continue;
      }
      if (mode === 'destination' && i === last) {
        formStore.patchStop(s.key, { plannedDeparture: null });
        continue;
      }
      formStore.patchStop(s.key, {
        plannedDeparture: null,
        plannedArrival: null,
      });
    }
  }

  function pruneManualExcept(mode: 'origin' | 'destination') {
    const originKey = stops.value[0]!.key;
    const destKey = stops.value[stops.value.length - 1]!.key;
    const next = new Set<string>();
    if (
      mode === 'origin' &&
      manualPlannedSlots.value.has(manualSlotKey(originKey, 'plannedDeparture'))
    ) {
      next.add(manualSlotKey(originKey, 'plannedDeparture'));
    }
    if (
      mode === 'destination' &&
      manualPlannedSlots.value.has(manualSlotKey(destKey, 'plannedArrival'))
    ) {
      next.add(manualSlotKey(destKey, 'plannedArrival'));
    }
    manualPlannedSlots.value = next;
  }

  function fillFromDestinationIfPossible() {
    const n = stops.value.length;
    const dest = stops.value[n - 1]!;
    const arr = dest.plannedArrival;
    const legDriveS = legDrivingSecondsFromRoutePlan(
      routePlan.value,
      n,
      selectedByPathId.value,
    );
    if (!arr || !allLegDurationsKnown(legDriveS)) {
      return;
    }
    const leg = legDriveS as number[];
    const snap = stops.value.map((s) => ({
      key: s.key,
      plannedDeparture: s.plannedDeparture,
      plannedArrival: s.plannedArrival,
    }));
    const dep0 = solveOriginDepartureMsForDestinationArrival(
      arr.getTime(),
      snap,
      leg,
      dwellMs.value,
      maxDrivingMs.value,
      restMs.value,
    );
    if (dep0 == null) {
      return;
    }
    formStore.patchStop(stops.value[0]!.key, {
      plannedDeparture: new Date(dep0),
    });
  }

  async function onAnchorConflictApply() {
    const mode = conflictResolutionChoice.value;
    autofillSettings.setLastConflictResolutionMode(mode);
    anchorConflictDialogVisible.value = false;

    const snapBefore = stops.value.map((s) => ({ ...s }));

    if (mode === 'origin') {
      const keepDep = snapBefore[0]?.plannedDeparture;
      clearAllTimesExcept('origin');
      pruneManualExcept('origin');
      if (keepDep) {
        formStore.patchStop(stops.value[0]!.key, {
          plannedDeparture: keepDep,
        });
      }
    } else {
      const keepArr = snapBefore[snapBefore.length - 1]?.plannedArrival;
      clearAllTimesExcept('destination');
      pruneManualExcept('destination');
      if (keepArr) {
        formStore.patchStop(
          stops.value[stops.value.length - 1]!.key,
          {
            plannedArrival: keepArr,
          },
        );
      }
      fillFromDestinationIfPossible();
    }

    lastAppliedFingerprint.value = '';
    prevStopsTimesFingerprint.value = '';
    await nextTick();
    runAutofillCore();
  }

  function onAnchorConflictCancel() {
    anchorConflictDialogVisible.value = false;
    suppressConflictDialog.value = true;
    void nextTick(() => runAutofillCore());
  }

  return {
    suppressPlannedTimeManualMark,
    onUserPlannedTimeEdit,
    anchorConflictDialogVisible,
    conflictResolutionChoice,
    onAnchorConflictApply,
    onAnchorConflictCancel,
  };
}
